import { DocumentPropertyFieldsAreaId } from 'types/dynamic-forms/DocumentPropertyFieldsAreaId';
import { JsonPointerPath } from 'types/dynamic-forms/JsonPointerPath';
import { StepRecord } from 'types/dynamic-forms/records/StepRecord';

type StepValidationStore = {
  getFieldsAreaId(path: JsonPointerPath): DocumentPropertyFieldsAreaId;
  getErrorMessagesIncludingNested(path: JsonPointerPath): Iterable<string>;
};

export function getStepValidationState(step: StepRecord, store: StepValidationStore): boolean {
  const propertyIds = collectPropertyIds(step.children);

  const scopedPropertyIds: string[] = [];
  for (const propertyId of propertyIds) {
    if (!step.isVerificationStep) {
      scopedPropertyIds.push(propertyId);
      continue;
    }

    try {
      if (store.getFieldsAreaId([propertyId]) === DocumentPropertyFieldsAreaId.Verification) {
        scopedPropertyIds.push(propertyId);
      }
    } catch {
      // A broken schema/store lookup is not proof that the step has no errors. Fail closed so the
      // UI never shows a green marker for a validation state it could not determine.
      return false;
    }
  }

  // A step with no (relevant) properties has nothing that can fail and is therefore complete.
  //
  // Completion is driven by validation errors, not editability. A step without errors is complete
  // even when all its fields are read-only. Read-only fields whose validation remains active still
  // prevent completion when they report an error.
  for (const propertyId of scopedPropertyIds) {
    try {
      for (const _ of store.getErrorMessagesIncludingNested([propertyId])) {
        return false;
      }
    } catch {
      // Unknown validation state must not be presented as successful validation.
      return false;
    }
  }

  return true;
}

function collectPropertyIds(schema: any): string[] {
  const result = new Set<string>();

  const visit = (node: any) => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (!node || typeof node !== 'object') {
      return;
    }

    if (typeof node.propertyId === 'string') {
      result.add(node.propertyId);
    }

    if (Array.isArray(node.children)) {
      visit(node.children);
    }
  };

  visit(schema);
  return [...result];
}
